import React, { useState } from 'react';
import { Upload, Search, MessageCircle, FileText, MapPin, Building, DollarSign, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';

const JobSeekerInterface = () => {
  const [resumeText, setResumeText] = useState('');
  const [jobQuery, setJobQuery] = useState('');
  const [jobResults, setJobResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Use environment variable or fallback to Render backend
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dlfi-backend.onrender.com';

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      setLoading(true);
      
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(`${API_BASE_URL}/upload-resume`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        setResumeText(response.data.resume_text);
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: `Resume uploaded successfully! I've analyzed your resume and can help you find matching jobs.`
        }]);
        
        // Auto-trigger job search with resume content
        try {
          const jobResponse = await axios.post(`${API_BASE_URL}/job-search`, {
            resume_text: response.data.resume_text,
            job_query: "software engineer" // Default query, user can modify
          });
          
          setJobResults(jobResponse.data.results || []);
          setChatMessages(prev => [...prev, { 
            role: "assistant", 
            content: `Found ${jobResponse.data.results?.length || 0} matching jobs based on your resume! I've displayed the top results above.` 
          }]);
        } catch (jobError) {
          console.error('Error in auto job search:', jobError);
        }
      } catch (error) {
        console.error('Error uploading resume:', error);
        
        // Provide more specific error messages
        let errorMessage = 'Sorry, there was an error processing your resume. Please try again.';
        
        if (error.response) {
          // Server responded with error
          if (error.response.data && error.response.data.detail) {
            errorMessage = `Error: ${error.response.data.detail}`;
          } else if (error.response.status === 400) {
            errorMessage = 'Invalid file. Please upload a valid PDF file.';
          } else if (error.response.status === 413) {
            errorMessage = 'File too large. Please upload a smaller PDF file.';
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Please try again later.';
          }
        } else if (error.request) {
          // Request made but no response (CORS or network issue)
          if (error.message && error.message.includes('CORS')) {
            errorMessage = 'CORS error: Backend is not allowing requests from this domain. Please check backend configuration.';
          } else {
            errorMessage = 'Unable to connect to the server. Please check your connection and try again.';
          }
        } else if (error.message) {
          // Error setting up request
          if (error.message.includes('CORS') || error.message.includes('access control')) {
            errorMessage = 'CORS error: Please check backend CORS configuration.';
          } else {
            errorMessage = `Error: ${error.message}`;
          }
        }
        
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: errorMessage
        }]);
      } finally {
        setLoading(false);
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  const handleJobSearch = async () => {
    if (!resumeText && !jobQuery) {
      alert('Please upload a resume or enter a job query');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/job-search`, {
        resume_text: resumeText,
        job_query: jobQuery
      });
        
        // Use the response data directly
        setJobResults(response.data.results || []);
        setChatMessages(prev => [...prev, { role: "assistant", content: `Found ${response.data.results?.length || 0} matching jobs for you! I've displayed the top results above.` }]);
      } catch (error) {
      console.error('Error searching jobs:', error);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, there was an error searching for jobs. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = {
      role: 'user',
      content: chatInput
    };

    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/chat`, {
        message: currentInput,
        context: resumeText
      });

      // Handle both success and error responses
      if (response.data && response.data.response) {
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.response
        }]);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error in chat:', error);
      
      // Extract error message from response if available
      let errorMessage = 'Sorry, I encountered an error. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        if (error.response.data && error.response.data.response) {
          errorMessage = error.response.data.response;
        } else if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication error. Please check API configuration.';
        } else if (error.response.status === 429) {
          errorMessage = 'Rate limit exceeded. Please try again in a moment.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.response.status === 503) {
          errorMessage = 'Service unavailable. Please check configuration.';
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage = 'Unable to connect to the server. Please check your connection.';
      } else if (error.message) {
        // Error setting up request
        errorMessage = `Error: ${error.message}`;
      }
      
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessage
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Find Your Dream Job</h1>
        <p className="text-secondary-600">Upload your resume and let AI find the perfect matches for you</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Resume Upload & Job Search */}
        <div className="space-y-6">
          {/* Resume Upload Section */}
          <div className="card">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
              <Upload className="h-5 w-5 mr-2 text-primary-600" />
              Upload your resume (PDF)
            </h2>
            
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors duration-200 ${
                isDragActive
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-secondary-300 hover:border-primary-400'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
              {isDragActive ? (
                <p className="text-primary-600 font-medium">Drop your resume here...</p>
              ) : (
                <div>
                  <p className="text-secondary-600 mb-2">
                    Drag & drop your resume here, or click to select
                  </p>
                  <p className="text-sm text-secondary-500">PDF files only</p>
                </div>
              )}
            </div>

            {uploadedFile && (
              <div className="mt-4 p-3 bg-primary-50 rounded-lg">
                <p className="text-primary-700 text-sm">
                  ✓ {uploadedFile.name} uploaded successfully
                </p>
              </div>
            )}
          </div>

          {/* Job Search Section */}
          <div className="card">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
              <Search className="h-5 w-5 mr-2 text-primary-600" />
              Job Search
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Job Title or Keywords (optional)
                </label>
                <input
                  type="text"
                  value={jobQuery}
                  onChange={(e) => setJobQuery(e.target.value)}
                  placeholder="e.g., Software Engineer, Python Developer..."
                  className="input-field"
                />
              </div>
              
              <button
                onClick={handleJobSearch}
                disabled={loading || (!resumeText && !jobQuery)}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Searching...' : 'Get Job Matches'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Job Results */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-primary-600" />
              Job Recommendations
            </h2>
            
            {jobResults.length > 0 ? (
              <div className="space-y-4">
                {jobResults.map((job) => (
                  <div key={job.id} className="border border-secondary-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-secondary-900">{job.title}</h3>
                      <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2 py-1 rounded-full">
                        {job.match} match
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm text-secondary-600 mb-2">
                      <Building className="h-4 w-4 mr-1" />
                      {job.company}
                    </div>
                    
                    <div className="flex items-center text-sm text-secondary-600 mb-2">
                      <MapPin className="h-4 w-4 mr-1" />
                      {job.location}
                    </div>
                    
                    <div className="flex items-center text-sm text-secondary-600 mb-2">
                      <DollarSign className="h-4 w-4 mr-1" />
                      {job.salary !== 'Salary not specified' ? job.salary : 'Salary not disclosed'}
                    </div>
                    
                    {/* Additional job information */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {job.employment_type && job.employment_type !== 'Not specified' && (
                        <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded">
                          {job.employment_type}
                        </span>
                      )}
                      {job.work_arrangements && job.work_arrangements !== 'Not specified' && (
                        <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded">
                          {job.work_arrangements}
                        </span>
                      )}
                      {job.level && job.level !== 'Not specified' && (
                        <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded">
                          {job.level}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-secondary-600 line-clamp-2 mb-2">
                      {job.description}
                    </p>
                    
                    {job.skills && job.skills !== 'Skills not specified' && (
                      <div className="mb-2">
                        <p className="text-xs text-secondary-500 mb-1">Key Skills:</p>
                        <p className="text-xs text-secondary-600 line-clamp-1">{job.skills}</p>
                      </div>
                    )}
                    
                    <button 
                      onClick={() => setSelectedJob(job)}
                      className="mt-3 text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      View Details →
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
                <p className="text-secondary-500">Upload your resume and search for jobs to see recommendations here</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chatbot Section */}
      <div className="card">
        <h2 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
          <MessageCircle className="h-5 w-5 mr-2 text-primary-600" />
          AI Assistant
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat History */}
          <div className="lg:col-span-2">
            <div className="bg-secondary-50 rounded-lg p-4 h-64 overflow-y-auto">
              {chatMessages.length > 0 ? (
                <div className="space-y-3">
                  {chatMessages.map((message, index) => (
                    <div key={index} className={`chat-message ${message.role}`}>
                      {message.content}
                    </div>
                  ))}
                  {loading && (
                    <div className="chat-message assistant">
                      <div className="flex items-center space-x-2">
                        <div className="animate-pulse">Thinking...</div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-secondary-500 py-8">
                  <MessageCircle className="h-8 w-8 mx-auto mb-2 text-secondary-300" />
                  <p>Start a conversation with our AI assistant</p>
                  {loading && (
                    <div className="mt-4 animate-pulse text-primary-600">
                      Processing your message...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Chat Input */}
          <div className="space-y-4">
            <form onSubmit={handleChatSubmit} className="space-y-3">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask me about job opportunities, resume tips, or career advice..."
                className="input-field h-32 resize-none"
                rows="4"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || loading}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
            
            <div className="text-xs text-secondary-500">
              <p>💡 Try asking:</p>
              <ul className="mt-2 space-y-1">
                <li>• "What skills should I highlight?"</li>
                <li>• "Find remote software jobs"</li>
                <li>• "How can I improve my resume?"</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-secondary-900">Job Details</h2>
              <button
                onClick={() => setSelectedJob(null)}
                className="text-secondary-500 hover:text-secondary-700 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Header Section */}
              <div>
                <h3 className="text-2xl font-semibold text-secondary-900 mb-2">{selectedJob.title}</h3>
                <div className="flex items-center text-lg text-secondary-600 mb-4">
                  <Building className="h-5 w-5 mr-2" />
                  {selectedJob.company}
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="bg-primary-100 text-primary-700 text-sm font-medium px-3 py-1 rounded-full">
                    {selectedJob.match} match
                  </span>
                  {selectedJob.employment_type && selectedJob.employment_type !== 'Not specified' && (
                    <span className="bg-secondary-100 text-secondary-700 text-sm px-3 py-1 rounded">
                      {selectedJob.employment_type}
                    </span>
                  )}
                  {selectedJob.work_arrangements && selectedJob.work_arrangements !== 'Not specified' && (
                    <span className="bg-secondary-100 text-secondary-700 text-sm px-3 py-1 rounded">
                      {selectedJob.work_arrangements}
                    </span>
                  )}
                </div>
              </div>

              {/* Key Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center text-secondary-600">
                  <MapPin className="h-5 w-5 mr-2 text-primary-600" />
                  <span className="font-medium">Location:</span>
                  <span className="ml-2">{selectedJob.location}</span>
                </div>
                <div className="flex items-center text-secondary-600">
                  <DollarSign className="h-5 w-5 mr-2 text-primary-600" />
                  <span className="font-medium">Salary:</span>
                  <span className="ml-2">{selectedJob.salary !== 'Salary not specified' ? selectedJob.salary : 'Not disclosed'}</span>
                </div>
                {selectedJob.level && selectedJob.level !== 'Not specified' && (
                  <div className="flex items-center text-secondary-600">
                    <span className="font-medium">Level:</span>
                    <span className="ml-2">{selectedJob.level}</span>
                  </div>
                )}
                {selectedJob.experience_years && selectedJob.experience_years !== 'Not specified' && (
                  <div className="flex items-center text-secondary-600">
                    <span className="font-medium">Experience Required:</span>
                    <span className="ml-2">{selectedJob.experience_years}</span>
                  </div>
                )}
                {selectedJob.industry && selectedJob.industry !== 'Not specified' && (
                  <div className="flex items-center text-secondary-600">
                    <span className="font-medium">Industry:</span>
                    <span className="ml-2">{selectedJob.industry}</span>
                  </div>
                )}
                {selectedJob.date_posted && selectedJob.date_posted !== 'Not specified' && (
                  <div className="flex items-center text-secondary-600">
                    <span className="font-medium">Posted:</span>
                    <span className="ml-2">{selectedJob.date_posted}</span>
                  </div>
                )}
              </div>

              {/* Key Responsibilities */}
              {selectedJob.key_responsibilities && 
                ((Array.isArray(selectedJob.key_responsibilities) && selectedJob.key_responsibilities.length > 0) ||
                (typeof selectedJob.key_responsibilities === 'string' && selectedJob.key_responsibilities.trim())) && (
                <div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-2">Key Responsibilities</h4>
                  <div className="bg-secondary-50 rounded-lg p-4">
                    {Array.isArray(selectedJob.key_responsibilities) ? (
                      <ul className="list-none space-y-2 text-secondary-700">
                        {selectedJob.key_responsibilities.map((responsibility, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-primary-600 mr-2">•</span>
                            <span>{responsibility}</span>
                          </li>
                        ))}
                      </ul>
                    ) : typeof selectedJob.key_responsibilities === 'string' ? (
                      <ul className="list-none space-y-2 text-secondary-700">
                        {selectedJob.key_responsibilities
                          .split('\n')
                          .filter(line => line.trim())
                          .map((line, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-primary-600 mr-2">•</span>
                              <span>{line.trim()}</span>
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <p className="text-secondary-700">{selectedJob.key_responsibilities}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Full Description */}
              <div>
                <h4 className="text-lg font-semibold text-secondary-900 mb-2">Full Job Description</h4>
                <div className="bg-secondary-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  <p className="text-secondary-700 whitespace-pre-wrap">
                    {selectedJob.full_description || selectedJob.description}
                  </p>
                </div>
              </div>

              {/* Skills */}
              {selectedJob.skills && selectedJob.skills !== 'Skills not specified' && (
                <div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-2">Required Skills</h4>
                  <div className="bg-secondary-50 rounded-lg p-4">
                    <p className="text-secondary-700">{selectedJob.skills}</p>
                  </div>
                </div>
              )}

              {/* Education */}
              {selectedJob.education && selectedJob.education !== 'Not specified' && (
                <div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-2">Education Requirements</h4>
                  <p className="text-secondary-700">{selectedJob.education}</p>
                </div>
              )}

              {/* Benefits */}
              {selectedJob.benefits && selectedJob.benefits !== 'Not specified' && (
                <div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-2">Benefits</h4>
                  <p className="text-secondary-700">{selectedJob.benefits}</p>
                </div>
              )}

              {/* Visa Sponsorship */}
              {selectedJob.visa_sponsorship && selectedJob.visa_sponsorship !== 'Not specified' && (
                <div>
                  <h4 className="text-lg font-semibold text-secondary-900 mb-2">Visa Sponsorship</h4>
                  <p className="text-secondary-700">{selectedJob.visa_sponsorship}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t border-secondary-200">
                {selectedJob.url && selectedJob.url !== '#' && (
                  <a
                    href={selectedJob.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    Apply on Company Website →
                  </a>
                )}
                <button
                  onClick={() => setSelectedJob(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSeekerInterface; 